import { UNKNOWN, type Citation, type Provenance, type Unknown } from '@asvise/facts'
import type { Domain, PersonId } from './types.ts'

/**
 * What a household policy is, and what its parts are called.
 *
 * The vocabulary half. It describes; it does not judge whether any of it is
 * enough, which is coverage.ts. The split is the same one the commercial engine
 * makes and for the same reason: an interface needs every word of this and none
 * of the comparison, and while both lived in one file the judgement shipped in
 * the bundle.
 *
 * Two things here have no commercial analogue at all, and both come straight
 * out of reading the demo whole life package.
 *
 * A BENEFIT POOL. Death, TPD and critical illness on that policy are three
 * names for claims against one S$100,000 sum. Paying the S$50,000 CI benefit
 * leaves S$50,000 of death cover, not S$150,000 of protection. Adding them is
 * the single most tempting error a reader of that schedule can make, so the pool
 * is modelled and the comparison is forbidden from summing across it.
 *
 * A PREMIUM TERM THAT IS NOT A COVER TERM. That policy is paid for over twenty
 * years and insures for life. Reading the twenty as an expiry would report a
 * family as losing their cover in 2041. They are two fields, and neither
 * defaults to the other.
 */

/** What a section of a household policy actually provides. */
export const COVERAGE_CODES = [
  'DEATH',
  'TPD',
  'CRITICAL_ILLNESS',
  'HOSPITAL_REIMBURSEMENT',
  'DISABILITY_INCOME',
  'LONG_TERM_CARE',
  'PERSONAL_ACCIDENT',
] as const
export type CoverageCode = (typeof COVERAGE_CODES)[number]

export const COVERAGE_LABEL: Record<CoverageCode, string> = {
  DEATH: 'Death benefit',
  TPD: 'Total and permanent disability',
  CRITICAL_ILLNESS: 'Critical illness',
  HOSPITAL_REIMBURSEMENT: 'Hospital and surgical',
  DISABILITY_INCOME: 'Disability income',
  LONG_TERM_CARE: 'Long-term care',
  PERSONAL_ACCIDENT: 'Personal accident',
}

/**
 * Which cover answers which domain.
 *
 * A reviewable table, not an inference, and the absences are deliberate.
 * Hospital reimbursement does not answer a critical illness recovery need: one
 * pays a hospital for a bill and the other replaces income a household has
 * stopped earning, and the demo scenario names counting the first as the second
 * as an acceptance failure.
 *
 * A TPD lump sum does not answer a disability income need either, which is why
 * DISABILITY_INCOME is listed beside TPD rather than instead of it.
 */
export const COVERAGE_FOR: Record<Domain, CoverageCode[]> = {
  EMERGENCY_LIQUIDITY: [],
  HOSPITAL_SURGICAL: ['HOSPITAL_REIMBURSEMENT'],
  DEATH_INCOME_CONTINUITY: ['DEATH'],
  DISABILITY_TPD: ['TPD', 'DISABILITY_INCOME'],
  CRITICAL_ILLNESS_RECOVERY: ['CRITICAL_ILLNESS'],
  LONG_TERM_CARE: ['LONG_TERM_CARE'],
}

/**
 * How a benefit relates to the others on its policy.
 *
 * ADDITIONAL is its own money. ACCELERATED is an early payment of something
 * else, and reduces what remains. The demo whole life policy has one of the
 * first and two of the second, and the difference between reading it correctly
 * and incorrectly is S$50,000 of protection a family does not have.
 */
export type BenefitBasis = 'ADDITIONAL' | 'ACCELERATED'

/** How long a claimant must live after qualifying, and how long before cover starts. */
export interface BenefitTiming {
  /** Days from commencement before a condition is covered at all. */
  waiting_period_days: number | null | Unknown
  /** Days the insured must survive after qualifying. */
  survival_period_days: number | null | Unknown
  /** Age at which this benefit stops, where the schedule states one. */
  expires_at_age: number | null | Unknown
  /** Date this benefit stops, where the schedule prints one. */
  expires_on: string | null | Unknown
  from: Provenance
}

/** Cost sharing on a reimbursement policy. Not a sum assured, and not comparable to one. */
export interface CostSharing {
  /** Borne by the household before the insurer pays anything, per policy year. */
  deductible: number | Unknown
  /** Share of the remainder the household bears, 0 to 1. */
  co_insurance: number | Unknown
  /** Whether a rider buys either of them down. */
  rider_reduces_cost_sharing: boolean | Unknown
  /** A cap on what the household can pay in a year, where the policy states one. */
  out_of_pocket_cap: number | null | Unknown
  from: Provenance
}

/** One thing a policy pays for, for one person. */
export interface Benefit {
  code: CoverageCode
  /**
   * Which household member this insures.
   *
   * Null until a person has said. A schedule names somebody in the insurer's
   * words, and matching that string to a member of this household is a judgement
   * only the household can make: it is stored as their answer, with the printed
   * name kept beside it in `insured_as_printed` so the two can be put side by
   * side. Nothing here ever guesses.
   */
  insured_person_id: PersonId | null
  /** The name exactly as the schedule prints it, evidence for the join above. */
  insured_as_printed: string | null
  /** The sum, where the benefit is a sum. Null on a reimbursement benefit. */
  amount: number | null | Unknown
  basis: BenefitBasis
  /**
   * The pool this draws from, where it shares one.
   *
   * Benefits with the same pool id are claims against a single sum, and the
   * comparison may never add two of them together. Null where the benefit
   * stands alone.
   */
  pool_id: string | null
  timing: BenefitTiming
  /** Reimbursement terms, on a hospital or medical benefit only. */
  cost_sharing?: CostSharing
  /** Annual reimbursement ceiling, on a reimbursement benefit. Never a sum assured. */
  annual_limit?: number | Unknown
  from: Provenance
  citation?: Citation
}

/**
 * A pool of cover several benefits claim against.
 *
 * `initial_amount` is what the pool held before any claim. What remains after an
 * accelerated payment is arithmetic the comparison does, not a figure read off a
 * schedule, because a schedule prints the position before claims.
 */
export interface BenefitPool {
  id: string
  initial_amount: number | Unknown
  /** Plain words for a reader: "death, disability and critical illness share S$100,000". */
  note: string
  from: Provenance
}

export type PremiumFrequency = 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'YEARLY' | 'SINGLE'

export interface Premium {
  amount: number | Unknown
  currency: string
  frequency: PremiumFrequency
  /**
   * Whether the rate is guaranteed for the term.
   *
   * Load-bearing here in a way it never was commercially: affordability is a
   * domain of its own, and a reviewable rate is the difference between a premium
   * a household can plan around and one that can move. The demo policy
   * guarantees the basic and TPD rates and explicitly does not guarantee the CI
   * rate, on the same page.
   */
  guaranteed: boolean | Unknown
  from: Provenance
}

export type PolicySource = 'PERSONAL' | 'EMPLOYER' | 'PUBLIC_SCHEME'

export interface Policy {
  id: string
  insurer: string
  product_name: string
  policy_number: string
  /** Who owns the contract. Not necessarily who it insures, and not necessarily who is paid. */
  policyholder_person_id: PersonId | null
  policyholder_as_printed: string | null
  /**
   * Who receives a death benefit.
   *
   * The framework's schema has a policyholder and an insured person and stops.
   * For a death benefit that is not enough: whether a household is actually
   * protected depends on who the money reaches, and these are three different
   * roles that a real schedule fills with three different names. Unknown is the
   * honest default and the demo policy records exactly that.
   */
  beneficiary_as_printed: string | null | Unknown
  source: PolicySource
  /**
   * Whether employer cover survives the job.
   *
   * Framework section 6.2: non-portable unless evidence confirms otherwise. So
   * the default is not "unknown", it is "no", and saying otherwise takes a
   * document.
   */
  portable: boolean | Unknown
  start_date: string | null
  /** Null where the policy is whole of life. Not the same field as the one below. */
  end_date: string | null | Unknown
  /**
   * When the premiums stop, which is not when the cover stops.
   *
   * Two fields because the demo policy is twenty years of premiums and a
   * lifetime of cover, and reading one as the other reports a family as
   * uninsured from 2041.
   */
  premium_term_end: string | null | Unknown
  premium: Premium
  pools: BenefitPool[]
  benefits: Benefit[]
  /** Exclusions and carve-outs quoted verbatim. Never summarised, never inferred. */
  /**
   * Why this document was marked at the gate, if it was.
   *
   * Framework section 5.2: a document failing one of its seven checks receives
   * DOCUMENT_REVIEW_REQUIRED, and unverified coverage must not be treated as
   * active. This field is what carries the first part, and `coverFor` is what
   * honours the second: a policy with anything in here cannot produce a figure
   * the comparison will treat as the cover available.
   *
   * Optional because it is newer than the rows already stored. A policy read
   * before the gate existed has not passed it and has not failed it, and
   * reading an absent field as an empty list says the right thing: nothing was
   * found against this document.
   */
  review_required?: string[]
  /**
   * Which reader produced this, where a reader did.
   *
   * Framework section 10.3's policy extraction version. Absent on a policy
   * entered by hand, which is the honest answer rather than a gap: nothing
   * extracted it, so no extraction version applies.
   */
  read_by_version?: string
  /**
   * When a person confirmed the reading, and nothing about who.
   *
   * Section 10.3's human review state, which for a household product is a
   * single question: has anybody looked at what was read out of the document
   * and said yes. Null means a policy sits on the record that nobody has
   * checked, which is a different thing from one that was checked and
   * questioned, and both are different from a document the gate marked.
   */
  confirmed_at?: string | null
  exclusions: { verbatim: string; citation?: Citation }[]
  /** What the reader could not settle, carried onto every finding this policy touches. */
  extraction_warnings: string[]
}

export const policyLabel = (p: Policy): string => `${p.insurer} (${p.product_name})`

/**
 * The benefits of one pool, which may never be added together.
 *
 * A function rather than a note in a comment, because the rule only holds if
 * every caller that wants a total asks the same question.
 */
export function poolMembers(policy: Policy, pool_id: string): Benefit[] {
  return policy.benefits.filter((b) => b.pool_id === pool_id)
}

/**
 * What one pool can pay for a single event, which is the pool, not the sum of
 * what its members are printed at.
 *
 * The demo policy prints S$100,000, S$100,000 and S$50,000 against death, TPD
 * and critical illness. It holds S$100,000.
 */
export function poolCapacity(policy: Policy, pool_id: string): number | Unknown {
  const pool = policy.pools.find((p) => p.id === pool_id)
  if (!pool) return UNKNOWN
  return pool.initial_amount
}

/**
 * What remains for the other members of a pool once one of them is paid.
 *
 * Not a prediction and not a claim about what will happen. It is what the
 * explanation needs in order to say "a S$50,000 illness payment would leave
 * S$50,000 for your family", which is the sentence the demo is built around and
 * the one a household cannot get from the schedule by reading it straight.
 */
export function remainingAfter(
  policy: Policy,
  pool_id: string,
  paid: number,
): number | Unknown {
  const capacity = poolCapacity(policy, pool_id)
  if (capacity === UNKNOWN) return UNKNOWN
  return Math.max(capacity - paid, 0)
}
